// ============================================================================
// Seguridad de la cuenta — cliente de `/security/*`.
// ============================================================================
// Verificación de correo y teléfono, métodos de 2FA, retos (al iniciar sesión y
// antes de una operación de riesgo) y la política de la plataforma.
//
// Contrato del backend: `modules/security/securityController.js`. Los errores
// traen `meta.code` estable (ver `SECURITY_ERROR_CODES`); el texto de
// `meta.message` ya viene traducido y es lo que se le muestra a la persona.
// ============================================================================

import i18n from "i18next";
import axios from "../utils/api";

/**
 * Las respuestas de `/security/*` traen el mensaje YA traducido y es lo que se
 * le muestra a la persona, así que tiene que salir en el idioma de la pantalla.
 * El interceptor general lo deduce de una llave de `localStorage` que la app no
 * siempre escribe y cae al idioma del navegador: aquí se manda explícito.
 */
const screenLang = () => ((i18n.language || "es").startsWith("en") ? "en" : "es");
type Config = Parameters<typeof axios.get>[1];
const withLang = (config?: Config): Config => ({ ...config, params: { ...config?.params, lang: screenLang() } });
const api = {
  get: (url: string, config?: Config) => axios.get(url, withLang(config)),
  delete: (url: string, config?: Config) => axios.delete(url, withLang(config)),
  post: (url: string, body?: unknown, config?: Config) => axios.post(url, body, withLang(config)),
  put: (url: string, body?: unknown, config?: Config) => axios.put(url, body, withLang(config)),
};

export type TwoFactorMethod = "totp" | "sms" | "email";
export type ChallengeMethod = TwoFactorMethod | "recovery";
export type ChallengePurpose = "login" | "step_up";
export type TwoFactorMode = "off" | "optional" | "required";
/** Qué se le pide a ESTE usuario: ver `enrollmentState` en el backend. */
export type EnrollmentState = "off" | "optional" | "grace" | "locked" | "ok";

export interface SecurityStatus {
  policy: {
    twoFactor: { mode: TwoFactorMode; deadline: string | null };
    stepUp: { enabled: boolean; windowMinutes: number };
  };
  enrollment: { state: EnrollmentState; deadline: string | null };
  /** `whatsapp`: plantilla de autenticación configurada (CC-CONSOLE#1550). */
  channels: { email: boolean; sms: boolean; whatsapp?: boolean };
  /**
   * Alta manual (CC-CONSOLE#1550): `required` = se dio de alta con correo y
   * contraseña; mientras `emailVerified` sea false, la plataforma lo manda a
   * /verificar.
   */
  signupVerification?: { required: boolean; emailVerified: boolean };
  email: { value: string; verified: boolean; verifiedAt: string | null };
  phone: { value: string; verified: boolean; verifiedAt: string | null };
  twoFactor: {
    enabled: boolean;
    methods: Record<TwoFactorMethod, { enabled: boolean; enabledAt: string | null }>;
    preferred: TwoFactorMethod | null;
    recoveryCodesLeft: number;
  };
  session: { twoFactorPending: boolean; stepUpValidUntil: string | null };
  challenge: { purpose: ChallengePurpose; methods: ChallengeMethod[]; preferred: TwoFactorMethod | null };
}

/** Estado más los códigos de respaldo, que sólo viajan la vez que se generan. */
export type SecurityStatusWithCodes = SecurityStatus & { recoveryCodes?: string[] | null };

export interface CodeSent {
  channel: "email" | "sms" | "whatsapp";
  sentTo: string;
  expiresInSeconds: number;
  resendInSeconds: number;
}

export interface TotpSetup {
  secret: string;
  otpauthUri: string;
  digits: number;
  periodSeconds: number;
}

export interface RiskyOperation {
  key: string;
  category: string;
  es: string;
  en: string;
  why: { es: string; en: string };
  defaultOn: boolean;
  /** `null` = sin excepción guardada. */
  override: boolean | null;
  on: boolean;
}

export interface SecurityPolicy {
  twoFactor: { mode: TwoFactorMode; deadline: string | null; exemptRoleIds: string[] };
  stepUp: { enabled: boolean; windowMinutes: number; operations: Record<string, boolean> };
  reason: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface SecurityPolicyPayload {
  /** `SECURITY_2FA_DISABLE=true` en el ambiente: todo apagado, diga lo que diga lo guardado. */
  envDisabled: boolean;
  policy: SecurityPolicy;
  effective: SecurityPolicy;
  channels: { email: boolean; sms: boolean };
  categories: { key: string; es: string; en: string }[];
  operations: RiskyOperation[];
  changed?: boolean;
}

export interface SecurityPolicyChange {
  twoFactor?: Partial<SecurityPolicy["twoFactor"]>;
  stepUp?: { enabled?: boolean; windowMinutes?: number; operations?: Record<string, boolean | null> };
  reason: string;
}

export interface PolicyAuditEntry {
  id: string;
  at: string | null;
  by: string | null;
  reason: string | null;
  from: Pick<SecurityPolicy, "twoFactor" | "stepUp">;
  to: Pick<SecurityPolicy, "twoFactor" | "stepUp">;
}

export const SECURITY_ERROR_CODES = {
  STEP_UP_REQUIRED: "STEP_UP_REQUIRED",
  TWO_FACTOR_CHALLENGE_REQUIRED: "TWO_FACTOR_CHALLENGE_REQUIRED",
  TWO_FACTOR_ENROLLMENT_REQUIRED: "TWO_FACTOR_ENROLLMENT_REQUIRED",
  CODE_COOLDOWN: "SECURITY_CODE_COOLDOWN",
  CODE_INVALID: "SECURITY_CODE_INVALID",
  EMAIL_VERIFICATION_REQUIRED: "EMAIL_VERIFICATION_REQUIRED",
  ALREADY_VERIFIED: "SECURITY_ALREADY_VERIFIED",
  PHONE_IN_USE: "SECURITY_PHONE_IN_USE",
} as const;

/** `{ meta, data: { message, data } }` → `data`. */
const unwrap = <T,>(response: { data?: { data?: { data?: T } } }): T => response.data?.data?.data as T;

export const fetchSecurityStatus = async (): Promise<SecurityStatus> =>
  unwrap<SecurityStatus>(await api.get("/security/status"));

export const sendEmailCode = async (): Promise<CodeSent> =>
  unwrap<CodeSent>(await api.post("/security/email/send-code", {}));

export const verifyEmail = async (code: string): Promise<SecurityStatus> =>
  unwrap<SecurityStatus>(await api.post("/security/email/verify", { code }));

/** Código al teléfono: por SMS (por omisión) o por WhatsApp (CC-CONSOLE#1550). */
export const sendPhoneCode = async (channel: "sms" | "whatsapp" = "sms"): Promise<CodeSent> =>
  unwrap<CodeSent>(await api.post("/security/phone/send-code", channel === "whatsapp" ? { channel } : {}));

export const verifyPhone = async (code: string): Promise<SecurityStatus> =>
  unwrap<SecurityStatus>(await api.post("/security/phone/verify", { code }));

export const setupTotp = async (): Promise<TotpSetup> =>
  unwrap<TotpSetup>(await api.post("/security/2fa/totp/setup", {}));

export const confirmTotp = async (code: string): Promise<SecurityStatusWithCodes> =>
  unwrap<SecurityStatusWithCodes>(await api.post("/security/2fa/totp/confirm", { code }));

export const enableCodeMethod = async (method: "email" | "sms"): Promise<SecurityStatusWithCodes> =>
  unwrap<SecurityStatusWithCodes>(await api.post("/security/2fa/methods/enable", { method }));

export const disableMethod = async (method: TwoFactorMethod): Promise<SecurityStatus> =>
  unwrap<SecurityStatus>(await api.delete("/security/2fa/methods", { params: { method } }));

export const setPreferredMethod = async (method: TwoFactorMethod): Promise<SecurityStatus> =>
  unwrap<SecurityStatus>(await api.put("/security/2fa/preferred", { method }));

export const regenerateRecoveryCodes = async (): Promise<SecurityStatusWithCodes> =>
  unwrap<SecurityStatusWithCodes>(await api.post("/security/2fa/recovery-codes", {}));

export const sendChallengeCode = async (method: "email" | "sms"): Promise<CodeSent> =>
  unwrap<CodeSent>(await api.post("/security/challenge/send", { method }));

export const verifyChallenge = async (method: ChallengeMethod, code: string): Promise<SecurityStatus> =>
  unwrap<SecurityStatus>(await api.post("/security/challenge/verify", { method, code }));

export const fetchSecurityPolicy = async (): Promise<SecurityPolicyPayload> =>
  unwrap<SecurityPolicyPayload>(await api.get("/security/policy"));

export const saveSecurityPolicy = async (change: SecurityPolicyChange): Promise<SecurityPolicyPayload> =>
  unwrap<SecurityPolicyPayload>(await api.put("/security/policy", change));

export const fetchSecurityPolicyAudit = async (): Promise<PolicyAuditEntry[]> =>
  unwrap<PolicyAuditEntry[]>(await api.get("/security/policy/audit")) || [];

type ApiError = {
  response?: { status?: number; data?: { meta?: { code?: string; message?: string; retryAfterSeconds?: number; attemptsLeft?: number } } };
};

/** Lo que trae el sobre de error de `/security/*`. */
export const securityErrorOf = (error: unknown) => {
  const meta = (error as ApiError)?.response?.data?.meta;
  return {
    code: typeof meta?.code === "string" ? meta.code : undefined,
    message: typeof meta?.message === "string" ? meta.message : undefined,
    retryAfterSeconds: typeof meta?.retryAfterSeconds === "number" ? meta.retryAfterSeconds : undefined,
    attemptsLeft: typeof meta?.attemptsLeft === "number" ? meta.attemptsLeft : undefined,
  };
};
